import { Pipe, PipeTransform, OnDestroy } from '@angular/core';
import { IQBoostViewStore } from '@maximizer/ai/data-access';
import { OpenEntityArgs } from '@maximizer/core/shared/ui/config';
import { AiMarkupUtils } from '../classes/ai-markup-utils';
import { TranslateService } from '@ngx-translate/core';
import {
  REFERENCE_LINK_CLASS,
  createEntityFromReferenceElement,
  createReferenceLink,
  isIgnoredReferenceType,
  readEntityKey,
} from './ref-link.utils';

@Pipe({ name: 'clickableRef', })
export class ClickableRefPipe implements PipeTransform, OnDestroy {
  // One listener for every instance: a pipe per answer would otherwise open the entity once per
  // answer on screen.
  private static clickHandler: ((event: MouseEvent) => void) | null = null;
  private static instances = 0;

  private readonly onFormatTemplate = '';

  constructor(
    private readonly store: IQBoostViewStore,
    private readonly translate: TranslateService,
  ) {
    this.setupEventHandlers();
    this.onFormatTemplate = this.translate.instant('ai.references.onFormat');
  }

  transform(value?: string | null): string {
    if (!value) return '';

    return AiMarkupUtils.replaceMaximizerReferences(value, (params) => {
      const exists = readEntityKey(params) && params['type'];

      if (exists) {
        if (isIgnoredReferenceType(params['type'])) {
          return null;
        }
        return createReferenceLink(params, this.onFormatTemplate, this.translate);
      }

      return null
    });
  }

  ngOnDestroy() {
    this.removeEventHandlers();
  }

  private setupEventHandlers(): void {
    ClickableRefPipe.instances++;
    if (ClickableRefPipe.clickHandler) return;

    const store = this.store;
    ClickableRefPipe.clickHandler = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target?.classList?.contains(REFERENCE_LINK_CLASS)) return;

      event.preventDefault();
      const entity = createEntityFromReferenceElement(target);
      if (entity) {
        store.triggerOpenEntity(new OpenEntityArgs(entity));
      }
    };

    document.addEventListener('click', ClickableRefPipe.clickHandler);
  }

  private removeEventHandlers(): void {
    ClickableRefPipe.instances = Math.max(0, ClickableRefPipe.instances - 1);
    if (ClickableRefPipe.instances === 0 && ClickableRefPipe.clickHandler) {
      document.removeEventListener('click', ClickableRefPipe.clickHandler);
      ClickableRefPipe.clickHandler = null;
    }
  }
}
